import React, { useEffect, useRef, useState } from 'react'
import ExplainSessionView from './ExplainSessionView'
import {
  EXPLAIN_SOLUTION_VIEW_CARDS,
  EXPLAIN_SOLUTION_VIEW_WORKSHEET
} from '../../helpers/explainNarrationPrefs'
import CelebrationOverlay from '../UiElements/CelebrationOverlay'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import './StudyHelperDemo.css'

// Phase 3 — Onboarding Demo. Locked decisions (see
// docs/AGENT_PROMPT_Parent_Signup_Phase3_Onboarding_Demo.md):
//   - Demo persistence: nowhere. Sandbox/fixtures only.
//   - Demo scope: Explain + check-answer.
//   - XP in demo: visual/educational only, no real gamification writes.
// Every value below is a fixture and nothing in this file ever calls a
// Cloud Function, touches Firestore, or references a real studentId. That
// is what makes it safe by construction rather than by convention: there is
// no student-scoped write path here to lock down in the first place, and no
// OCR/AI spend to attribute to a child's account.
const DEMO_XP_AMOUNT = 25
const MAX_CHECK_ATTEMPTS = 3

const EXPLAIN_FIXTURE = {
  question: 'Solve for $x$: $3x + 7 = 22$',
  topic: 'Algebra',
  finalAnswer: '$x = 5$',
  steps: [
    {
      solution: '3x + 7 = 22',
      explanation: "Here's the equation we're starting with. The goal is to get x alone on one side."
    },
    {
      solution: '3x = 15',
      explanation: 'Subtract 7 from both sides — whatever we do to one side, we do to the other.'
    },
    {
      solution: 'x = 5',
      explanation: 'Divide both sides by 3. Now x is alone, so that\'s the final answer.'
    }
  ]
}

const CHECK_ANSWER_FIXTURE = {
  question: 'Solve for $x$: $2x - 4 = 10$',
  topic: 'Algebra',
  correctAnswers: ['7', 'x=7'],
  correctFeedback: 'Correct! 2(7) − 4 = 10. Nicely done.',
  incorrectFeedback: 'Not quite yet — add 4 to both sides first, then divide by 2.',
  revealedAnswer: 'x = 7'
}

const INTRO_STEPS = [
  {
    emoji: '👋',
    title: 'Try it together',
    body: "Grab a minute with your child — this is what they'll see in the Study Helper when they're stuck on a problem."
  },
  {
    emoji: '🧭',
    title: 'Two ways to help',
    body: '"Explain" walks through a solution step by step. "Check Answer" grades work your child already did. Hand them the phone for the next part!'
  },
  {
    emoji: '✨',
    title: 'Watch for the XP',
    body: "When your child finishes a real problem, they earn XP and see a little celebration — that's the moment that keeps them coming back."
  }
]

// Loose match: strips spaces/case so "x = 7", "X=7", and "7" all count.
const normalizeAnswer = (value) => value.trim().toLowerCase().replace(/\s+/g, '')

const IntroSlides = ({ stepIndex, onNext, onBack, onSkip }) => {
  const step = INTRO_STEPS[stepIndex]
  const isLast = stepIndex === INTRO_STEPS.length - 1
  return (
    <div className="qn-demo-intro">
      <div className="qn-demo-intro-emoji">{step.emoji}</div>
      <h2 className="qn-demo-intro-title">{step.title}</h2>
      <p className="qn-demo-intro-body">{step.body}</p>
      <div className="qn-demo-intro-dots" aria-hidden="true">
        {INTRO_STEPS.map((s, i) => (
          <span key={s.title} className={`qn-demo-dot ${i === stepIndex ? 'active' : i < stepIndex ? 'done' : ''}`} />
        ))}
      </div>
      <div className="qn-demo-intro-actions">
        <button
          type="button"
          className="qn-demo-btn ghost"
          onClick={onBack}
          style={{ visibility: stepIndex === 0 ? 'hidden' : 'visible' }}
        >
          ← Back
        </button>
        <button type="button" className="qn-demo-btn primary" onClick={onNext}>
          {isLast ? "Let's try it →" : 'Next →'}
        </button>
      </div>
      <button type="button" className="qn-demo-skip-link" onClick={onSkip}>
        Skip tutorial, go straight to the demo
      </button>
    </div>
  )
}

const ChoiceScreen = ({ onSelectExplain, onSelectCheckAnswer, notice, onSkip }) => (
  <div className="qn-demo-choice">
    <div className="qn-demo-mascot">
      <img src="/favicon.png" alt="" />
    </div>
    <h2 className="qn-demo-choice-title">What would you like to try?</h2>
    {notice && <p className="qn-demo-choice-notice">{notice}</p>}
    <p className="qn-demo-choice-subtitle">
      {notice
        ? 'Pick one to try. Nothing is saved.'
        : 'Pick one below — hand the phone to your child for this part.'}
    </p>
    <div className="qn-demo-choice-buttons">
      <button type="button" className="qn-demo-choice-btn" onClick={onSelectExplain}>
        <span className="qn-demo-choice-btn-title">Explain this problem</span>
        <span className="qn-demo-choice-btn-desc">See a step-by-step walkthrough</span>
      </button>
      <button type="button" className="qn-demo-choice-btn" onClick={onSelectCheckAnswer}>
        <span className="qn-demo-choice-btn-title">Check my answer</span>
        <span className="qn-demo-choice-btn-desc">Try solving it, then see if you're right</span>
      </button>
    </div>
    {onSkip && (
      <button type="button" className="qn-demo-skip-btn" onClick={onSkip}>
        Skip
      </button>
    )}
  </div>
)

const CheckAnswerDemo = ({
  answerInput,
  onChangeAnswer,
  feedback,
  attempts,
  revealed,
  onSubmit,
  onFinish,
  onBack
}) => (
  <div className="explain-session qn-demo-check">
    <div className="explain-paper">
      <div className="explain-paper-header">
        <div className="explain-paper-heading">
          <span className="explain-paper-eyebrow">Check your answer</span>
          <span className="explain-topic-tag">{CHECK_ANSWER_FIXTURE.topic}</span>
        </div>
        <button type="button" className="explain-start-over-btn" onClick={onBack}>
          ← Back
        </button>
      </div>

      <div className="explain-problem">
        <div className="explain-block-label">Problem</div>
        <div className="explain-math-block">
          <MixedContentPreview content={CHECK_ANSWER_FIXTURE.question} fontSize="1.35rem" />
        </div>
      </div>

      <div className="qn-demo-answer-row">
        <label className="explain-block-label" htmlFor="qn-demo-answer-input">Your answer</label>
        <input
          id="qn-demo-answer-input"
          type="text"
          className="qn-demo-answer-input"
          value={answerInput}
          onChange={(e) => onChangeAnswer(e.target.value)}
          placeholder="e.g. x = 7"
          disabled={revealed}
          autoComplete="off"
        />
      </div>
      <p className="qn-demo-answer-note">
        In the real Study Helper, your child snaps a photo of their handwritten work instead —
        this demo just types it in to keep things quick.
      </p>
    </div>

    {feedback && (
      <div className={`qn-demo-feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`} role="status">
        <span aria-hidden="true">{feedback.isCorrect ? '✓' : '✗'}</span> {feedback.message}
      </div>
    )}

    <div className="explain-controls">
      {!revealed ? (
        <button
          type="button"
          className="explain-control-btn next"
          onClick={onSubmit}
          disabled={!answerInput.trim()}
        >
          {attempts > 0 ? `Check again (${attempts + 1} of ${MAX_CHECK_ATTEMPTS})` : 'Check answer'}
        </button>
      ) : (
        <button type="button" className="explain-control-btn finish" onClick={onFinish}>
          Finish
        </button>
      )}
    </div>
  </div>
)

const FinishedScreen = ({ path, onTryOther, onClose, audience }) => (
  <div className="qn-demo-finished">
    <div className="qn-demo-mascot cheering">
      <img src="/favicon.png" alt="" />
    </div>
    <h2 className="qn-demo-finished-title">🎉 Nice work!</h2>
    <p className="qn-demo-finished-body">
      {audience === 'student'
        ? "That's what it looks like when you finish a problem in Quillnote."
        : "That's what it looks like when your child finishes a problem in Quillnote."}
    </p>
    <div className="qn-demo-finished-xp">
      Students earn XP when they finish — like this! ✨
    </div>
    <p className="qn-demo-finished-disclaimer">
      This was a demo. Nothing was saved, and no real student earned XP.
    </p>
    <div className="qn-demo-finished-actions">
      <button type="button" className="qn-demo-btn ghost" onClick={onTryOther}>
        Try {path === 'explain' ? 'Check Answer' : 'Explain'} too
      </button>
      <button type="button" className="qn-demo-btn primary" onClick={onClose}>
        Done
      </button>
    </div>
    {audience !== 'student' && (
      <p className="qn-demo-checklist-hint">✓ "Completed Demo" checked off your Getting Started checklist</p>
    )}
  </div>
)

// onClose: parent left the demo (dismissed choice, closed mid-walkthrough, or
//   clicked Done). Never persists anything.
// onComplete: called exactly once, the moment the parent/child finish a full
//   walkthrough of either fixture problem (Explain finish, or a resolved
//   Check Answer attempt). This — not merely opening the demo or skipping the
//   intro slides — is what "tutorial marked complete" means for the
//   "Completed Demo" checklist item and demoTutorialCompleted: the intro
//   slides are skippable chrome with no credit of their own.
const STUDENT_TUTORIAL_NOTICE = 'This is a practice tutorial. It does not use one of your weekly sessions.'

const StudyHelperDemo = ({ onClose, onComplete, audience = 'parent' }) => {
  const forStudent = audience === 'student'
  const [phase, setPhase] = useState(forStudent ? 'choice' : 'intro')
  const [introIndex, setIntroIndex] = useState(0)
  const [explainStepIndex, setExplainStepIndex] = useState(0)
  // In-memory only. The demo never writes a student preference.
  const [solutionView, setSolutionView] = useState(EXPLAIN_SOLUTION_VIEW_CARDS)
  const [answerInput, setAnswerInput] = useState('')
  const [checkAttempts, setCheckAttempts] = useState(0)
  const [checkFeedback, setCheckFeedback] = useState(null)
  const [revealedAnswer, setRevealedAnswer] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const [completedPath, setCompletedPath] = useState(null)
  const completeCalledRef = useRef(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    if (!showCelebration) return
    const timer = setTimeout(() => setShowCelebration(false), 2600)
    return () => clearTimeout(timer)
  }, [showCelebration])

  const markComplete = () => {
    if (completeCalledRef.current) return
    completeCalledRef.current = true
    onComplete()
  }

  const goToChoice = () => setPhase('choice')

  const handleIntroNext = () => {
    if (introIndex >= INTRO_STEPS.length - 1) {
      goToChoice()
    } else {
      setIntroIndex(i => i + 1)
    }
  }

  const handleIntroBack = () => {
    if (introIndex > 0) setIntroIndex(i => i - 1)
  }

  const startExplain = () => {
    setExplainStepIndex(0)
    setPhase('explain')
  }

  const startCheckAnswer = () => {
    setAnswerInput('')
    setCheckAttempts(0)
    setCheckFeedback(null)
    setRevealedAnswer(false)
    setPhase('check')
  }

  const handleExplainFinish = () => {
    setCompletedPath('explain')
    setShowCelebration(true)
    markComplete()
    setPhase('finished')
  }

  const handleCheckAnswerSubmit = () => {
    const trimmed = answerInput.trim()
    if (!trimmed) return

    const isCorrect = CHECK_ANSWER_FIXTURE.correctAnswers.includes(normalizeAnswer(trimmed))
    const nextAttempts = checkAttempts + 1
    setCheckAttempts(nextAttempts)

    const exhausted = nextAttempts >= MAX_CHECK_ATTEMPTS
    setCheckFeedback({
      isCorrect,
      message: isCorrect
        ? CHECK_ANSWER_FIXTURE.correctFeedback
        : exhausted
          ? `${CHECK_ANSWER_FIXTURE.incorrectFeedback} The answer is ${CHECK_ANSWER_FIXTURE.revealedAnswer}.`
          : CHECK_ANSWER_FIXTURE.incorrectFeedback
    })

    if (isCorrect || exhausted) {
      setRevealedAnswer(true)
    }
  }

  const handleCheckAnswerFinish = () => {
    setCompletedPath('check-answer')
    setShowCelebration(true)
    markComplete()
    setPhase('finished')
  }

  const handleTryOther = () => {
    setShowCelebration(false)
    if (completedPath === 'explain') {
      startCheckAnswer()
    } else {
      startExplain()
    }
  }

  return (
    <div className="qn-demo-overlay" role="presentation">
      <div className="qn-demo-shell" role="dialog" aria-modal="true" aria-labelledby="qn-demo-title">
        <div className="qn-demo-topbar">
          <span id="qn-demo-title" className="qn-demo-badge">Demo — nothing is saved</span>
          <button type="button" className="qn-demo-close" onClick={onClose} aria-label="Close demo">
            ×
          </button>
        </div>

        <div className="qn-demo-body">
          {phase === 'intro' && (
            <IntroSlides
              stepIndex={introIndex}
              onNext={handleIntroNext}
              onBack={handleIntroBack}
              onSkip={goToChoice}
            />
          )}

          {phase === 'choice' && (
            <ChoiceScreen
              onSelectExplain={startExplain}
              onSelectCheckAnswer={startCheckAnswer}
              notice={forStudent ? STUDENT_TUTORIAL_NOTICE : ''}
              onSkip={forStudent ? onClose : null}
            />
          )}

          {phase === 'explain' && (
            <ExplainSessionView
              question={EXPLAIN_FIXTURE.question}
              topic={EXPLAIN_FIXTURE.topic}
              steps={EXPLAIN_FIXTURE.steps}
              finalAnswer={EXPLAIN_FIXTURE.finalAnswer}
              currentStepIndex={explainStepIndex}
              explanationText={EXPLAIN_FIXTURE.steps[explainStepIndex]?.explanation || ''}
              askEnabled={false}
              speechSupported={false}
              finishing={false}
              completed={false}
              errorMessage={null}
              onPrevious={() => setExplainStepIndex(i => Math.max(0, i - 1))}
              onNext={() => setExplainStepIndex(i => Math.min(EXPLAIN_FIXTURE.steps.length - 1, i + 1))}
              onFinish={handleExplainFinish}
              onAsk={() => {}}
              onStartOver={() => setExplainStepIndex(0)}
              groupedSolution={solutionView === EXPLAIN_SOLUTION_VIEW_WORKSHEET}
              solutionView={solutionView}
              onChangeSolutionView={setSolutionView}
            />
          )}

          {phase === 'check' && (
            <CheckAnswerDemo
              answerInput={answerInput}
              onChangeAnswer={setAnswerInput}
              feedback={checkFeedback}
              attempts={checkAttempts}
              revealed={revealedAnswer}
              onSubmit={handleCheckAnswerSubmit}
              onFinish={handleCheckAnswerFinish}
              onBack={goToChoice}
            />
          )}

          {phase === 'finished' && (
            <FinishedScreen
              path={completedPath}
              audience={audience}
              onTryOther={handleTryOther}
              onClose={onClose}
            />
          )}
        </div>
      </div>

      <CelebrationOverlay showConfetti={showCelebration} showXpPop={showCelebration} xpAmount={DEMO_XP_AMOUNT} />
    </div>
  )
}

export default StudyHelperDemo
